import { useEffect, useState } from 'react'
import { colors } from '@fitpet/constants-global'
import { useSelector } from 'react-redux'
import { StyledDivider } from '@/components/common/divider'
import { Spinner } from '@/components/common/etc'
import { InputCheckbox } from '@/components/common/inputs'
import { CartEmpty } from '@/components/domains/cart/CartEmpty'
import { CartErrorModal } from '@/components/domains/cart/CartErrorModal'
import CartOrderButton from '@/components/domains/cart/CartOrderButton'
import { CartProductsV2 } from '@/components/domains/cart/CartProductV2'
import { CartSummary } from '@/components/domains/cart/CartSummary'
import { CartUnApplyCouponModal } from '@/components/domains/cart/CartUnApplyCouponModal'
import { CartUnApplyCouponWithAmountModal } from '@/components/domains/cart/CartUnApplyCouponWithAmountModal'
import { CartWelcomeDealItemErrorModal } from '@/components/domains/cart/CartWelcomeDealItemErrorModal'
import { CheckedCartItemDeleteModal } from '@/components/domains/cart/CheckedCartItemDeleteModal'
import { CartEventProductWidgetV2 } from '@/components/domains/cart/cart-event-product-widget-v2'
import { CartOrderCouponNudgeButton } from '@/components/domains/cart/cart-order-coupon-nudge-button'
import { CartRecommendationSectionV2 } from '@/components/domains/cart/cart-recommendation-section-v2'
import CartRecommendedProductsV2 from '@/components/domains/cart/cart-recommended-products-v2'
import { GlobalSizeEnum } from '@/constants/global-size.enum'
import { ShippingForCart, useCartContents } from '@/containers/cart/useCartContents'
import { usePetType } from '@/containers/hooks'
import { UserContainer } from '@/containers/users/UserContainer'
import { AdPetType } from '@/graphql/generated/schemas'
import { RootState } from '@/stores/store'
import styles from './CartContents.module.css'

interface EmptyCartProps {
  isPersonalizationTab?: boolean
}

const EmptyCart = ({ isPersonalizationTab }: EmptyCartProps) => {
  return (
    <>
      <CartEmpty isPersonalizationTab={isPersonalizationTab} title="장바구니 상품" />
      {/* 최근 본 상품 */}
      <CartRecommendationSectionV2 />
      <StyledDivider height="4rem" backgroundColor={colors.WHITE} />
    </>
  )
}

interface CartContentsProps {
  onViewCartTracker: ({ productAmount, shippings }: { productAmount: number; shippings: ShippingForCart[] }) => void
}

export const CartContents = ({ onViewCartTracker }: CartContentsProps) => {
  const [isDeleteSelectedModalOpen, setIsDeleteSelectedModalOpen] = useState(false)
  const {
    allCheckProduct,
    isAllChecked,
    deleteSelected,
    confirmOrder,
    handleConfirmCartError,
    isCartError,
    handleRemoveParamsCouponByCouponIds,
    allCountItemCount,
    welcomeDealError,
    handleConfirmWelcomeDealError,
    handleConfirmCartCouponError,
    appCouponData,
  } = useCartContents()
  const { getPetType } = usePetType()
  const cartData = useSelector((state: RootState) => state.reduxDataReducers.cartData)
  const { me, meIsLoading: isMeLoading } = UserContainer.useContainer()

  const handleClickDeleteSelected = () => {
    setIsDeleteSelectedModalOpen(true)
  }

  const handleCloseDeleteSelectedModal = () => {
    setIsDeleteSelectedModalOpen(false)
  }

  const handleClickAllCheck = (isChecked: boolean) => {
    allCheckProduct(isChecked)
  }

  useEffect(() => {
    if (!cartData || !me) {
      return
    }
    onViewCartTracker({ productAmount: cartData.productAmount, shippings: cartData.shippings })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData])

  if (isMeLoading) {
    return <Spinner wrap />
  }

  if (!me) {
    return <EmptyCart isPersonalizationTab />
  }

  if (cartData && cartData.shippings.length === 0) {
    return <EmptyCart />
  }

  if (!cartData) {
    return null
  }

  const cartOptionIds =
    cartData.shippings.flatMap((group) => group.orderItems.map((item) => item.productOptionId)) || []

  return (
    <>
      <div className={styles.cartContent}>
        <div className={styles.selectAll}>
          <div className={styles.selectBoxLabel}>
            <InputCheckbox
              id="allCheck"
              checked={isAllChecked}
              size={GlobalSizeEnum.Lg}
              onChange={() => handleClickAllCheck(!isAllChecked)}
            />
            <span className="font-body2-regular gray-500">전체 선택</span>
          </div>
          <div className={styles.deleteSelected} onClick={handleClickDeleteSelected}>
            <span style={{ textDecoration: 'underline' }} className="font-body2-regular gray-500">
              선택삭제
            </span>
          </div>
        </div>
        <CartProductsV2 />
        <CartSummary />
        <StyledDivider height="0.8rem" backgroundColor={colors.GRAY_70} />
        {/* 함께 구매하면 좋은 상품 */}
        <CartRecommendedProductsV2 cartProductOptionsIdInCart={cartOptionIds} />

        <CartEventProductWidgetV2
          key={getPetType() === AdPetType.Dog ? 'sale-dog' : 'sale-cat'}
          widgetTitle="유통기한 임박 상품"
          eventId={getPetType() === AdPetType.Dog ? 'sale-dog' : 'sale-cat'}
          isLastSection={true}
        />

        <CartOrderButton
          totalPaymentAmount={cartData.amount}
          handleOrderClick={confirmOrder}
          checkedProductOptionsCount={allCountItemCount}
        />
      </div>

      {/* 장바구니 주문쿠폰 넛지 버튼*/}
      {!!appCouponData && (
        <CartOrderCouponNudgeButton
          adId={appCouponData.adBySlug?.id}
          adName={appCouponData.adBySlug?.name}
          adSlug={appCouponData.adBySlug?.slug}
          isDisabled={appCouponData.adBySlug?.isAllCouponsDownloaded}
        />
      )}

      {/* 적용 불가 쿠폰 에러 모달*/}
      <CartErrorModal isShow={isCartError} onConfirm={handleConfirmCartError} />

      <CartUnApplyCouponModal excludedCoupons={cartData.excludedCoupons} onConfirm={handleConfirmCartCouponError} />
      {!!cartData.excludedCoupons.length && (
        <CartUnApplyCouponWithAmountModal
          excludedCoupons={cartData.excludedCoupons}
          onConfirm={handleRemoveParamsCouponByCouponIds}
        />
      )}
      <CheckedCartItemDeleteModal
        isShow={isDeleteSelectedModalOpen}
        onClose={handleCloseDeleteSelectedModal}
        onConfirm={deleteSelected}
      />
      {welcomeDealError && (
        <CartWelcomeDealItemErrorModal
          onClose={handleConfirmWelcomeDealError}
          isShow={!!welcomeDealError}
          modalData={welcomeDealError}
        />
      )}
    </>
  )
}
